'use client';

import {type FC, Suspense, useEffect} from 'react';
import {
	keepPreviousData,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import clsx from 'clsx';
import {useSearchParams} from 'next/navigation';

import type {ArticlesApiResponse} from '@/app/(frontend)/api/articles/route';
import {Alert} from '@/modules/finkn/components/alert';
import type {
	AreaFilters,
	FieldOfAreaFilters,
} from '@/modules/finkn/shared/components/filters/category-filters';
import {CategoryFilters} from '@/modules/finkn/shared/components/filters/category-filters';
import {
	QUERY_PARAM_DELIMITER,
	searchParamsToFilters,
} from '@/modules/finkn/shared/components/filters/search-params-filter';
import type {KeyedArray} from '@/modules/foundation/shared/types';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';
import {ARTICLE_PAGE_SIZE} from '@/shared/constants';

import type {ArticleDetails} from '../../shared/types';
import {ArticleCard, ArticleCardSkeleton} from '../article-card';
import {PaginationWithLinks} from '../pagination';

interface Props extends ContentBlockContainerProps {
	limit?: number;
	initialArticles?: KeyedArray<ArticleDetails>;
	totalArticles: number;
	fieldOfAreaFilters?: FieldOfAreaFilters[];
	areaFilters: AreaFilters[];
	insideToc?: boolean;
}

const fetchArticles = async (
	page = 0,
	areas?: string[],
	fieldOfAreas?: string[],
): Promise<ArticlesApiResponse> => {
	const areasString = areas?.join(QUERY_PARAM_DELIMITER) ?? '';
	const fieldOfAreasString = fieldOfAreas?.join(QUERY_PARAM_DELIMITER) ?? '';

	let query = `/api/articles?page=${page}`;

	if (areas?.length) {
		query = query + `&areas=${areasString}`;
	}

	if (fieldOfAreas?.length) {
		query = query + `&fieldOfAreas=${fieldOfAreasString}`;
	}

	const response = await fetch(query);
	return await response.json();
};

const ArticleListPaginated: FC<Props> = ({
	initialArticles,
	totalArticles,
	fieldOfAreaFilters,
	areaFilters,
	options,
	insideToc,
}) => {
	const queryClient = useQueryClient();
	const searchParams = useSearchParams();

	const filters = searchParamsToFilters(searchParams);

	const page = parseInt(searchParams.get('page') || '1');

	const {status, data, error, isFetching, isPlaceholderData} = useQuery({
		queryKey: ['articles', page, filters.area, filters.fieldOfArea],
		queryFn: () => fetchArticles(page, filters.area, filters.fieldOfArea),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000, // 5 minutes
		initialData:
			page === 1 &&
			initialArticles &&
			totalArticles &&
			!filters.area.length &&
			!filters.fieldOfArea.length
				? {
						articles: initialArticles,
						hasMore: initialArticles.length < totalArticles,
						totalArticles,
						areaFilters,
					}
				: undefined,
	});

	// Prefetch the next page!
	useEffect(() => {
		if (!isPlaceholderData && data?.hasMore) {
			queryClient.prefetchQuery({
				queryKey: ['articles', page + 1],
				queryFn: () => fetchArticles(page + 1),
				staleTime: 5 * 60 * 1000, // 5 minutes
			});
		}
	}, [data, isPlaceholderData, page, queryClient]);

	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<section className={clsx('grid', '@2xl:grid-cols-3', 'gap-10')}>
					<aside className={clsx('@2xl:col-span-1')}>
						<Suspense>
							<CategoryFilters
								fieldOfAreaFilters={fieldOfAreaFilters}
								areaFilters={data?.areaFilters}
							/>
						</Suspense>
					</aside>
					<div className={clsx('@2xl:col-span-2')}>
						{
							// Since the last page's data potentially sticks around between page requests,
							// we can use `isFetching` to show a background loading
							// indicator since our `status === 'pending'` state won't be triggered
						}
						{status === 'pending' || isFetching ? (
							<div className={clsx('grid', 'gap-10')}>
								{initialArticles?.map((article) => (
									<ArticleCardSkeleton key={article._key} />
								))}
							</div>
						) : status === 'error' ? (
							<div>Error: {error.message}</div>
						) : (
							// `data` will either resolve to the latest page's data
							// or if fetching a new page, the last successful page's data

							<ul className={clsx('grid', 'gap-10')}>
								{data?.articles && data.articles.length ? (
									<>
										{data.articles.map((article) => {
											if (!article) {
												return null;
											}

											return (
												<li key={article._key}>
													<ArticleCard
														article={article}
														headingLevel={2}
													/>
												</li>
											);
										})}
									</>
								) : (
									<Alert title="Ingen nyheter" role="status">
										Valgte filtre har ingen nyheter!
									</Alert>
								)}
							</ul>
						)}
						{data?.totalArticles ? (
							<div className={clsx('mt-10')}>
								<PaginationWithLinks
									page={page}
									pageSize={ARTICLE_PAGE_SIZE}
									totalCount={data.totalArticles}
								/>
							</div>
						) : null}
					</div>
				</section>
			</div>
			<ReactQueryDevtools initialIsOpen={false} />
		</ContentBlockContainer>
	);
};

export default ArticleListPaginated;
